/**
 * Feature detection, isolated so tests can fake it.
 *
 * CRITICAL iOS CONSTRAINT (BRIEF <platform_precedence>): `SpeechRecognition` works in an iOS
 * Safari tab but NOT in an installed home-screen web app. This app therefore ships no manifest
 * and no install prompt. If standalone mode is detected anyway, speech modes are disabled and a
 * one-line notice tells the learner to reopen in a Safari tab. It is a notice, never a modal, and
 * it never blocks play.
 */

export interface PlatformCapabilities {
  /** True when either `SpeechRecognition` or `webkitSpeechRecognition` exists AND we are not standalone. */
  speechAvailable: boolean;
  /** True when the page is running as an installed web app. */
  standalone: boolean;
  speechSynthesisAvailable: boolean;
}

interface StandaloneNavigator extends Navigator {
  standalone?: boolean;
}

export function detectStandalone(win: Window = window): boolean {
  const nav = win.navigator as StandaloneNavigator;
  if (nav.standalone === true) return true;
  return typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches;
}

export function detectSpeechRecognition(win: Window = window): boolean {
  const w = win as unknown as Record<string, unknown>;
  return typeof w['SpeechRecognition'] === 'function' || typeof w['webkitSpeechRecognition'] === 'function';
}

export function detectCapabilities(win: Window = window): PlatformCapabilities {
  const standalone = detectStandalone(win);
  return {
    standalone,
    // Degrade to typed production silently wherever absent.
    speechAvailable: detectSpeechRecognition(win) && !standalone,
    speechSynthesisAvailable: 'speechSynthesis' in win,
  };
}
